import { useState } from 'react'
import { createUser, updateUser } from '@/api/users'
import { getApiErrorMessage } from '@/lib/apiError'
import { ROLE_LABELS, isUserRole } from '@/lib/users'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { UpdateUserPayload } from '@/api/users'
import type { User, UserRole } from '@/api/types'

interface UserFormCardProps {
  user: User | null
  roles: UserRole[]
  currentUserId: number
  onSaved: (user: User) => void
  onCancel: () => void
}

export function UserFormCard({ user, roles, currentUserId, onSaved, onCancel }: UserFormCardProps): React.ReactElement {
  const isEditing = user !== null

  const [displayName, setDisplayName] = useState(user?.display_name ?? '')
  const [email, setEmail] = useState(user?.email ?? '')
  // Ingen förvald roll vid skapande - en admin som missar dropdownen ska inte råka skapa en superadmin.
  const [role, setRole] = useState<UserRole | ''>(user?.role ?? '')
  const [password, setPassword] = useState('')
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const isEditingSelf = isEditing && user.id === currentUserId
  const isPasswordFieldVisible = !isEditing || isChangingPassword
  // Redigerar man en användare vars roll inte är tilldelningsbar för den inloggade (t.ex. en
  // superadmin), ska dropdownen ändå visa den riktiga rollen istället för en tom platshållare.
  const selectableRoles = isEditing && !roles.includes(user.role) ? [user.role, ...roles] : roles

  const handleSave = async (): Promise<void> => {
    const trimmedName = displayName.trim()
    const trimmedEmail = email.trim()

    if (trimmedName.length === 0) {
      setErrorMessage('Namn måste fyllas i.')
      return
    }

    if (trimmedEmail.length === 0) {
      setErrorMessage('E-post måste fyllas i.')
      return
    }

    if (isPasswordFieldVisible && password.length === 0) {
      setErrorMessage('Lösenord måste fyllas i.')
      return
    }

    if (role === '') {
      setErrorMessage('Välj en roll.')
      return
    }

    setErrorMessage(null)
    setIsSaving(true)

    try {
      if (!isEditing) {
        const created = await createUser({ display_name: trimmedName, email: trimmedEmail, password, role })

        onSaved(created)
        return
      }

      const payload: UpdateUserPayload = {}

      if (trimmedName !== user.display_name) payload.display_name = trimmedName
      if (trimmedEmail !== user.email) payload.email = trimmedEmail
      if (role !== user.role) payload.role = role
      if (isChangingPassword) payload.password = password

      if (Object.keys(payload).length === 0) {
        setErrorMessage('Inga ändringar att spara.')
        return
      }

      const updated = await updateUser(user.id, payload)

      onSaved(updated)
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, 'Kunde inte spara användaren just nu.'))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Card className="shadow-none border-border/80">
      <CardHeader>
        <CardTitle>{isEditing ? `Redigera ${user.display_name}` : 'Ny användare'}</CardTitle>
        <CardDescription>{isEditing ? 'Ändra uppgifter för användaren.' : 'Skapa ett nytt personalkonto.'}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="user-display-name">Namn</Label>
          <Input
            id="user-display-name"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="user-email">E-post</Label>
          <Input
            id="user-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="user-role">Roll</Label>
          <Select
            value={role}
            onValueChange={(value) => {
              if (isUserRole(value)) setRole(value)
            }}
          >
            <SelectTrigger
              id="user-role"
              className="w-full"
            >
              <SelectValue placeholder="Välj roll" />
            </SelectTrigger>
            <SelectContent>
              {selectableRoles.map((roleOption) => (
                <SelectItem
                  key={roleOption}
                  value={roleOption}
                >
                  {ROLE_LABELS[roleOption]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          {isEditing && !isChangingPassword && (
            <>
              <Label>Lösenord</Label>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsChangingPassword(true)}
              >
                Byt lösenord
              </Button>
            </>
          )}

          {isPasswordFieldVisible && (
            <>
              <Label htmlFor="user-password">{isEditing ? 'Nytt lösenord' : 'Lösenord'}</Label>
              <Input
                id="user-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              {isEditing && (
                <p className="text-xs text-muted-foreground">
                  Användaren loggas ut från alla enheter och måste logga in igen med det nya lösenordet.
                </p>
              )}
            </>
          )}
        </div>

        {isEditingSelf && <p className="text-xs text-muted-foreground sm:col-span-2">Du redigerar ditt eget konto.</p>}

        {errorMessage && <p className="text-sm text-destructive sm:col-span-2">{errorMessage}</p>}

        <div className="flex gap-2 sm:col-span-2">
          <Button
            disabled={isSaving}
            onClick={() => void handleSave()}
          >
            Spara
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={isSaving}
            onClick={onCancel}
          >
            Avbryt
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
